import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { searchCalls, getCallDetail, getCallFormAttributes } from '@services/api';
import './CallSearchTab.css';

export default function CallSearchTab() {
  const { data: formAttributes } = useQuery({
    queryKey: ['callFormAttributes'],
    queryFn: getCallFormAttributes,
    staleTime: Infinity,
    retry: 1,
  });
  const callCategories = formAttributes?.categories ?? [];
  const dispositionReasons = formAttributes?.dispositionReasons ?? [];

  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [showFilters, setShowFilters] = useState(true);
  const [twilioAccountSid, setTwilioAccountSid] = useState(null);

  // Expanded row state
  const [selectedCallId, setSelectedCallId] = useState(null);
  const [callDetail, setCallDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [filters, setFilters] = useState({
    startDate: new Date(Date.now() - 24 * 60 * 60 * 1000),
    endDate: new Date(),
    phoneNumber: '',
    callSid: '',
    patientName: '',
    coachName: '',
    category: '',
    dispositionReason: '',
  });

  useEffect(() => {
    try {
      const authToken = sessionStorage.getItem('authToken');
      if (authToken) {
        const payload = authToken.split('.')[1];
        if (payload) {
          const decoded = JSON.parse(atob(payload));
          if (decoded.twilio_account_sid) {
            setTwilioAccountSid(decoded.twilio_account_sid);
          }
        }
      }
    } catch (err) {
      console.debug('Could not extract account SID from token:', err);
    }
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await searchCalls({
        ...filters,
        page,
        pageSize,
      });
      setCalls(response.calls || []);
      setTotalPages(response.totalPages || 0);
      setTotalCount(response.totalCount || 0);
    } catch (err) {
      setError('Failed to search calls: ' + (err.message || 'Unknown error'));
      console.error('Error searching calls:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filters, page, pageSize]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPage(1);
  };

  const handleClearFilters = () => {
    setFilters({
      startDate: new Date(Date.now() - 24 * 60 * 60 * 1000),
      endDate: new Date(),
      phoneNumber: '',
      callSid: '',
      patientName: '',
      coachName: '',
      category: '',
      dispositionReason: '',
    });
    setPage(1);
  };

  const handleRowClick = async (contactId) => {
    if (selectedCallId === contactId) {
      setSelectedCallId(null);
      setCallDetail(null);
      return;
    }

    setSelectedCallId(contactId);
    setCallDetail(null);
    setDetailLoading(true);

    try {
      const detail = await getCallDetail(contactId);
      setCallDetail(detail);
    } catch (err) {
      console.error('Error fetching call detail:', err);
      setCallDetail({ error: 'Failed to load call detail' });
    } finally {
      setDetailLoading(false);
    }
  };

  const getTwilioConsoleUrl = (callSid) => {
    if (!callSid) return null;
    if (!/^CA[0-9a-fA-F]{32}$/.test(callSid)) return null;

    const frameUrl = `/console/voice/calls/logs/${callSid}`;
    const base = new URL('https://console.twilio.com/us1/monitor/logs/calls');
    base.searchParams.set('frameUrl', frameUrl);
    base.searchParams.set('x-target-region', 'us1');
    base.searchParams.set('__override_layout__', 'embed');
    base.searchParams.set('bifrost', 'true');

    if (twilioAccountSid) {
      base.searchParams.set('accountSid', twilioAccountSid);
    }
    return base.toString();
  };

  const CallSidLink = ({ sid }) => {
    if (!sid) return null;
    return (
      <a
        href={getTwilioConsoleUrl(sid)}
        target="_blank"
        rel="noopener noreferrer"
        className="call-sid-link"
        title="View in Twilio Console"
        onClick={(e) => e.stopPropagation()}
      >
        {sid}
      </a>
    );
  };

  const formatTimestamp = (isoString) => {
    if (!isoString) return '-';
    try {
      return new Date(isoString).toLocaleString();
    } catch {
      return '-';
    }
  };

  const getCategoryColor = (cat) => {
    switch (cat) {
      case 'Outgoing Call': return '#1976d2';
      case 'Incoming Call': return '#43a047';
      case 'Left Voicemail': return '#f57c00';
      case 'IVR Resupply': return '#7b1fa2';
      default: return '#757575';
    }
  };

  return (
    <div className="call-search-tab">
      <div className="call-search-header">
        <h2>Call Search</h2>
        <button
          className="filter-toggle-button"
          onClick={() => setShowFilters(!showFilters)}
        >
          {showFilters ? 'Hide Filters' : 'Show Filters'}
        </button>
      </div>

      {showFilters && (
        <div className="filters-panel">
          <div className="filter-group">
            <label>Start Date</label>
            <input
              type="datetime-local"
              value={filters.startDate.toISOString().slice(0, 16)}
              onChange={(e) => handleFilterChange('startDate', new Date(e.target.value))}
            />
          </div>
          <div className="filter-group">
            <label>End Date</label>
            <input
              type="datetime-local"
              value={filters.endDate.toISOString().slice(0, 16)}
              onChange={(e) => handleFilterChange('endDate', new Date(e.target.value))}
            />
          </div>
          <div className="filter-group">
            <label>Phone Number</label>
            <input
              type="text"
              placeholder="Search by phone number"
              value={filters.phoneNumber}
              onChange={(e) => handleFilterChange('phoneNumber', e.target.value)}
            />
          </div>
          <div className="filter-group">
            <label>Call SID</label>
            <input
              type="text"
              placeholder="Twilio Call SID"
              value={filters.callSid}
              onChange={(e) => handleFilterChange('callSid', e.target.value)}
            />
          </div>
          <div className="filter-group">
            <label>Patient Name</label>
            <input
              type="text"
              placeholder="Search by patient name"
              value={filters.patientName}
              onChange={(e) => handleFilterChange('patientName', e.target.value)}
            />
          </div>
          <div className="filter-group">
            <label>Coach Name</label>
            <input
              type="text"
              placeholder="Search by coach name"
              value={filters.coachName}
              onChange={(e) => handleFilterChange('coachName', e.target.value)}
            />
          </div>
          <div className="filter-group">
            <label>Category</label>
            <select
              value={filters.category}
              onChange={(e) => handleFilterChange('category', e.target.value)}
            >
              <option value="">All Categories</option>
              {callCategories.map((cat) => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label>Disposition</label>
            <select
              value={filters.dispositionReason}
              onChange={(e) => handleFilterChange('dispositionReason', e.target.value)}
            >
              <option value="">All Dispositions</option>
              {dispositionReasons.map((d) => (
                <option key={d.id} value={d.reason}>{d.reason}</option>
              ))}
            </select>
          </div>
          <button className="clear-filters-button" onClick={handleClearFilters}>
            Clear Filters
          </button>
        </div>
      )}

      <div className="call-search-container">
        {error && <div className="search-error-message">{error}</div>}

        {loading ? (
          <div className="loading">Loading calls...</div>
        ) : calls.length === 0 ? (
          <div className="no-results">No calls found for the selected filters</div>
        ) : (
          <>
            <div className="call-results-table">
              <table>
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Direction</th>
                    <th>Category</th>
                    <th>Patient</th>
                    <th>Coach</th>
                    <th>Company</th>
                    <th>Phone</th>
                    <th>Answered</th>
                    <th>Disposition</th>
                  </tr>
                </thead>
                <tbody>
                  {calls.map((call) => (
                    <>
                      <tr
                        key={call.contactId}
                        className={`call-row ${selectedCallId === call.contactId ? 'expanded' : ''}`}
                        onClick={() => handleRowClick(call.contactId)}
                      >
                        <td className="call-timestamp">{formatTimestamp(call.timestamp)}</td>
                        <td>{call.direction || '-'}</td>
                        <td>
                          <span
                            className="category-badge"
                            style={{ backgroundColor: getCategoryColor(call.category) }}
                          >
                            {call.category || '-'}
                          </span>
                        </td>
                        <td className="call-person">{call.patientName || '-'}</td>
                        <td className="call-person">{call.coachName || '-'}</td>
                        <td className="call-company">{call.companyName || '-'}</td>
                        <td className="call-phone">
                          {call.origination && call.destination
                            ? `${call.origination} → ${call.destination}`
                            : call.origination || call.destination || '-'}
                        </td>
                        <td>
                          {call.answered === true && <span className="answered-badge yes">Yes</span>}
                          {call.answered === false && <span className="answered-badge no">No</span>}
                          {call.answered == null && '-'}
                        </td>
                        <td className="call-disposition">{call.dispositionReason || '-'}</td>
                      </tr>

                      {selectedCallId === call.contactId && (
                        <tr key={`detail-${call.contactId}`} className="detail-row">
                          <td colSpan="9">
                            {detailLoading && (
                              <div className="detail-loading">Loading detail...</div>
                            )}
                            {callDetail?.error && (
                              <div className="detail-error">{callDetail.error}</div>
                            )}
                            {callDetail && !callDetail.error && !detailLoading && (
                              <CallDetailPanel detail={callDetail} CallSidLink={CallSidLink} formatTimestamp={formatTimestamp} />
                            )}
                          </td>
                        </tr>
                      )}
                    </>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="pagination">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Previous
              </button>
              <div className="page-info">
                Page {page} of {totalPages} ({totalCount} total calls)
              </div>
              <select
                value={pageSize}
                onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                className="page-size-select"
              >
                <option value={10}>10 per page</option>
                <option value={25}>25 per page</option>
                <option value={50}>50 per page</option>
                <option value={100}>100 per page</option>
              </select>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
              >
                Next
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function CallDetailPanel({ detail, CallSidLink, formatTimestamp }) {
  const timingPairs = [
    ['Client Leg', detail.clientLegStartTime, detail.clientLegEndTime],
    ['Outside Party Leg', detail.outsidePartyLegStartTime, detail.outsidePartyLegEndTime],
    ['Queue', detail.queueStartTime, detail.queueEndTime],
    ['Conference', detail.conferenceStartTime, detail.conferenceEndTime],
    ['Connection', detail.connectionStartTime, detail.connectionEndTime],
  ].filter(([, start, end]) => start || end);

  return (
    <div className="call-detail">
      <div className="detail-grid">
        <div className="detail-section">
          <h4>Call IDs</h4>
          {detail.callSid && (
            <p><strong>Call SID:</strong> <CallSidLink sid={detail.callSid} /></p>
          )}
          {detail.parentCallSid && (
            <p><strong>Parent Call SID:</strong> <CallSidLink sid={detail.parentCallSid} /></p>
          )}
          <p><strong>Contact ID:</strong> {detail.contactId}</p>
          {detail.contactGuid && (
            <p><strong>Contact GUID:</strong> <span className="guid-text">{detail.contactGuid}</span></p>
          )}
        </div>

        <div className="detail-section">
          <h4>People</h4>
          <p><strong>Patient:</strong> {detail.patientName || 'N/A'}</p>
          <p><strong>Coach:</strong> {detail.coachName || 'N/A'}</p>
          <p><strong>Company:</strong> {detail.companyName || 'N/A'}</p>
        </div>

        <div className="detail-section">
          <h4>Call Info</h4>
          <p><strong>Direction:</strong> {detail.direction || '-'}</p>
          <p><strong>Category:</strong> {detail.category || '-'}</p>
          <p><strong>Answered:</strong> {detail.answered === true ? 'Yes' : detail.answered === false ? 'No' : '-'}</p>
          <p><strong>Success:</strong> {detail.success ? 'Yes' : 'No'}</p>
          {detail.isIvr && <p><strong>IVR Call:</strong> Yes</p>}
          {detail.isAutomation && <p><strong>Automated:</strong> Yes</p>}
          {detail.timesInQueue > 0 && <p><strong>Times in Queue:</strong> {detail.timesInQueue}</p>}
        </div>

        <div className="detail-section">
          <h4>Disposition</h4>
          <p><strong>Reason:</strong> {detail.dispositionReason || 'N/A'}</p>
          {detail.transferReason && <p><strong>Transfer Reason:</strong> {detail.transferReason}</p>}
          {detail.dispositionNotes && <p><strong>Notes:</strong> {detail.dispositionNotes}</p>}
        </div>
      </div>

      {timingPairs.length > 0 && (
        <div className="detail-section timing-section">
          <h4>Timing</h4>
          <div className="timing-grid">
            {timingPairs.map(([label, start, end]) => (
              <div key={label} className="timing-pair">
                <span className="timing-label">{label}:</span>
                <span className="timing-value">
                  {formatTimestamp(start)} {end ? `→ ${formatTimestamp(end)}` : '(ongoing)'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {detail.notes && (
        <div className="detail-section">
          <h4>Notes</h4>
          <pre className="notes-text">{detail.notes}</pre>
        </div>
      )}

      {detail.recordingSid && (
        <div className="detail-section">
          <h4>Recording</h4>
          <p><strong>Recording SID:</strong> <span className="guid-text">{detail.recordingSid}</span></p>
        </div>
      )}

      {detail.twilioEnrichment && (
        <div className="detail-section twilio-section">
          <h4>Twilio Data</h4>
          <p><strong>Status:</strong> {detail.twilioEnrichment.status || '-'}</p>
          {detail.twilioEnrichment.price && (
            <p><strong>Price:</strong> ${detail.twilioEnrichment.price}</p>
          )}
          {detail.twilioEnrichment.duration != null && (
            <p><strong>Duration:</strong> {Math.floor(detail.twilioEnrichment.duration / 60)}m {detail.twilioEnrichment.duration % 60}s</p>
          )}
        </div>
      )}
    </div>
  );
}
